import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, UserRound } from 'lucide-react';
import { Page, PageHeader } from '@ampm-7en-ai/ui/components/Page';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { Avatar } from '@ampm-7en-ai/ui/components/Avatar';
import { DetailRow, DetailRows, DetailSection } from '@ampm-7en-ai/ui/components/DetailList';
import { Field } from '@ampm-7en-ai/ui/components/Input';
import { Textarea } from '@ampm-7en-ai/ui/components/Textarea';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { Skeleton } from '@ampm-7en-ai/ui/components/Skeleton';
import {
  CONTACT_STATUSES, contactLabel, contactName, useContactRequest, useUpdateContactRequest,
  type ContactRequest, type ContactStatus,
} from '@/hooks/useContactRequests';
import { useToast } from '@/hooks/use-toast';
import { formatRelativeTime } from '@/lib/utils';

/**
 * One message from the public contact form. The form collects very little —
 * a name, a way to reply, and the message — so the message leads and the
 * reply details sit beside it, each one actionable rather than plain text.
 */

const STATUS_TONE: Record<ContactStatus, 'warning' | 'accent' | 'success' | 'neutral'> = {
  OPEN: 'warning', IN_PROGRESS: 'accent', RESOLVED: 'success', CLOSED: 'neutral',
};

const statusLabel = (status: ContactStatus) =>
  CONTACT_STATUSES.find((entry) => entry.value === status)?.label ?? status;

const date = (value?: string | null) => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]?.toUpperCase() ?? '').join('') || '?';

function RequestEditor({ request }: { request: ContactRequest }) {
  const [saved, setSaved] = useState(request);
  const [status, setStatus] = useState<ContactStatus>(request.status);
  const [notes, setNotes] = useState(request.admin_notes);
  const [failure, setFailure] = useState('');
  const update = useUpdateContactRequest();
  const { toast } = useToast();
  const dirty = status !== saved.status || notes !== saved.admin_notes;
  const sender = contactName(request);
  // The form makes both contact fields optional, so say up front how they can be reached.
  const reach = [request.email ? 'email' : null, request.phone_number ? 'phone' : null].filter(Boolean);

  useEffect(() => {
    if (!dirty && !update.isPending) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, update.isPending]);

  const save = async () => {
    setFailure('');
    const fields: Partial<Pick<ContactRequest, 'status' | 'admin_notes'>> = {};
    if (status !== saved.status) fields.status = status;
    if (notes !== saved.admin_notes) fields.admin_notes = notes;
    try {
      const result = await update.mutateAsync({ id: request.id, fields });
      setSaved(result);
      setStatus(result.status);
      setNotes(result.admin_notes);
      toast({ title: 'Contact request saved' });
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'The contact request could not be saved.');
    }
  };

  return (
    <>
      <Link to="/v2/contact-requests"
        className="-ml-1 mb-3 inline-flex items-center gap-1.5 text-ui-sm text-ui-fg-muted hover:text-ui-fg"
        onClick={(event) => {
          if (update.isPending || (dirty && !window.confirm('Discard unsaved changes to this request?'))) event.preventDefault();
        }}><ArrowLeft className="h-4 w-4" aria-hidden />Back to contact requests</Link>

      <PageHeader
        // A sender's name or address can be long; wrapping beats cutting it off.
        className="[&_h1]:whitespace-normal [&_h1]:break-words"
        title={contactLabel(request)}
        meta={<Pill tone={STATUS_TONE[saved.status]}>{statusLabel(saved.status)}</Pill>}
        description={`Request #${request.id} · Received ${formatRelativeTime(request.created_at).toLowerCase()}`}
        actions={request.email ? (
          <Button variant="secondary" asChild>
            <a href={`mailto:${request.email}`}><Mail aria-hidden />Reply by email</a>
          </Button>
        ) : undefined}
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <Panel padding="md" className="min-w-0">
            <h2 className="mb-3 text-ui-base font-medium text-ui-fg">Message</h2>
            {request.message
              ? <p className="whitespace-pre-wrap break-words text-ui-sm leading-relaxed text-ui-fg">{request.message}</p>
              : <p className="text-ui-sm text-ui-fg-muted">No message was included with this request.</p>}
          </Panel>
        </div>

        <div className="min-w-0 space-y-4">
          <Panel padding="md" className="min-w-0">
            <form className="space-y-5" onSubmit={(event) => { event.preventDefault(); if (dirty && !update.isPending) void save(); }}>
              <div>
                <h2 className="text-ui-base font-medium text-ui-fg">Manage request</h2>
                <p className="mt-1 text-ui-xs text-ui-fg-muted">Track whether this message has been answered and record what was said.</p>
              </div>
              {failure && <Callout tone="danger" title="Could not save request"><p>{failure}</p></Callout>}
              <Field label="Status" htmlFor="contact-status">
                <Select id="contact-status" options={[...CONTACT_STATUSES]} value={status} disabled={update.isPending}
                  onValueChange={(value) => {
                    const selected = CONTACT_STATUSES.find((entry) => entry.value === value);
                    if (selected) setStatus(selected.value);
                  }} />
              </Field>
              <Field label="Admin notes" htmlFor="contact-admin-notes" hint="Internal notes, visible to superadmins only.">
                <Textarea id="contact-admin-notes" rows={8} value={notes} onChange={(event) => setNotes(event.target.value)} disabled={update.isPending} />
              </Field>
              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-ui-border pt-4">
                {dirty && <span className="mr-auto text-ui-xs text-ui-fg-muted">Unsaved changes</span>}
                <Button type="submit" variant="primary" disabled={!dirty || update.isPending}>
                  {update.isPending ? 'Saving...' : 'Save changes'}
                </Button>
              </div>
            </form>
          </Panel>

          <Panel padding="none" className="min-w-0">
            <DetailSection title="Sender">
              <div className="mb-3 flex items-center gap-3">
                <Avatar initials={sender ? initials(sender) : undefined}
                  icon={sender ? undefined : <UserRound className="h-4 w-4" aria-hidden />} />
                <div className="min-w-0">
                  <p className="truncate text-ui-sm font-medium text-ui-fg">{sender || 'Name not provided'}</p>
                  <p className="flex items-center gap-1 truncate text-ui-xs text-ui-fg-muted">
                    <Mail className="h-3 w-3 shrink-0" aria-hidden />
                    {reach.length ? `Reachable by ${reach.join(' and ')}` : 'No reply details provided'}
                  </p>
                </div>
              </div>
              <DetailRows>
                <DetailRow label="Email" wrap>
                  {request.email
                    ? <a className="text-ui-accent-text hover:underline" href={`mailto:${request.email}`}>{request.email}</a>
                    : 'Not provided'}
                </DetailRow>
                <DetailRow label="Phone" wrap>
                  {request.phone_number
                    ? <a className="text-ui-accent-text hover:underline" href={`tel:${request.phone_number.replace(/[^+\d]/g, '')}`}>{request.phone_number}</a>
                    : 'Not provided'}
                </DetailRow>
              </DetailRows>
            </DetailSection>

            <DetailSection title="Timeline">
              <DetailRows>
                <DetailRow label="Received" wrap>{date(request.created_at)}</DetailRow>
                <DetailRow label="Updated" wrap>{date(saved.updated_at)}</DetailRow>
                <DetailRow label="Resolved" wrap>{date(saved.resolved_at)}</DetailRow>
              </DetailRows>
            </DetailSection>

            <DetailSection title="Status history">
              <DetailRows>
                <DetailRow label="Current status">
                  <Pill tone={STATUS_TONE[saved.status]}>{statusLabel(saved.status)}</Pill>
                </DetailRow>
                <DetailRow label="Last status change by" wrap>{saved.status_changed_by || 'Not recorded'}</DetailRow>
                <DetailRow label="Last status change" wrap>{date(saved.status_changed_at)}</DetailRow>
              </DetailRows>
            </DetailSection>

            <DetailSection title="Origin">
              <DetailRows>
                <DetailRow label="IP address" wrap>
                  <span className="font-mono text-ui-xs">{request.ip_address || 'Not recorded'}</span>
                </DetailRow>
              </DetailRows>
            </DetailSection>
          </Panel>
        </div>
      </div>
    </>
  );
}

export default function ContactRequestDetailV2() {
  const { requestId } = useParams();
  const { data, isLoading, isError, error, refetch } = useContactRequest(requestId);
  return <Page>
    {isLoading ? (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    ) : isError ? (
      <>
        <Link to="/v2/contact-requests" className="mb-4 inline-block text-ui-sm text-ui-accent-text">Back to contact requests</Link>
        <Callout tone="danger" title="Could not load contact request">
          <p>{error instanceof Error ? error.message : 'The request failed.'}</p>
          <Button className="mt-3" onClick={() => void refetch()}>Try again</Button>
        </Callout>
      </>
    ) : data ? <RequestEditor key={data.id} request={data} /> : null}
  </Page>;
}
